import React from 'react';
import _ from 'lodash';
import eyeIcon from '@/shared/ui/icons/Components/Status/eye-icon.svg';
import eyeSlashIcon from '@/shared/ui/icons/Components/Status/eye-slash-icon.svg';
import { ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';

type ToggleShChannelProps = {
  visibleChannels: number[];
  disabled: boolean;
  onChange: (channelIdx: number) => void;
};

/**
 * Renders the CH1, CH2 and CH3 show-hide toggles for the ECG strip viewer. Computes which channels are currently visible with lodash includes to pick the open or crossed eye icon, disables each button through disableCondition so the last visible channel cannot be hidden, and reports clicks through onChange with the channel index.
 *
 * @param props - Toggle props carrying visibleChannels, disabled and onChange.
 * @returns The rendered three-channel visibility toggle row.
 */
const ToggleShChannel = ({ visibleChannels, disabled, onChange }: ToggleShChannelProps) => {
  const isSingleChannel = visibleChannels.length <= 1;
  const foundCH1 = _.includes(visibleChannels, 0);
  const foundCH2 = _.includes(visibleChannels, 1);
  const foundCH3 = _.includes(visibleChannels, 2);
  /**
   * Decides whether one channel toggle button in the ECG toolbar must stay disabled. Returns true when only a single channel is visible and it is this one, or when the viewer config reports no original data for the channel index; otherwise it falls back to the disabled prop.
   *
   * @param channelIdx - Zero-based index of the channel whose button is being rendered.
   * @returns True when the channel button must be disabled.
   */
  const disableCondition = (channelIdx: number) => {
    // ECG_VIEWER_CONFIG_STORE has no `originalChannels` field (only DEFAULT_ECG_VIEWER_CONFIG
    // does) — this always reads undefined, pre-existing behaviour, not fixed here.
    if (
      (isSingleChannel && channelIdx === visibleChannels[0]) ||
      !(ECG_VIEWER_CONFIG_STORE as any).originalChannels[channelIdx]
    ) {
      return true;
    }
    return disabled;
  };
  return (
    <div className="toggle-sh-channel">
      <button disabled={disableCondition(0)} onClick={() => onChange(0)}>
        <img src={foundCH1 ? eyeIcon : eyeSlashIcon} alt="eye-icon" />
        CH1
      </button>
      <button disabled={disableCondition(1)} onClick={() => onChange(1)}>
        <img src={foundCH2 ? eyeIcon : eyeSlashIcon} alt="eye-icon" />
        CH2
      </button>
      <button disabled={disableCondition(2)} onClick={() => onChange(2)}>
        <img src={foundCH3 ? eyeIcon : eyeSlashIcon} alt="eye-icon" />
        CH3
      </button>
    </div>
  );
};

export default ToggleShChannel;
